import { useCallback, lazy, Suspense } from 'react';

import { useDeviceType } from '@picsart/design-system/hooks/device';
import { isPortfolioApp as checkIsPortfolioApp } from '@/utils/environment/surface';
import useAuth from '@/hooks/useAuth';
import { pushBrowserNav } from '@/hooks/useBrowserHistory';
import openRegistrationModal from '@/utils/user/openRegistrationModal';
import { useAppStore, setAppStore, type IAppStore } from '@/store';
import { Screen } from '@/types';
import Header from '@/components/Header';
import AppLayout from '@/components/AppLayout';

import ResultOverlay from '../ResultOverlay';
import useAppNavigation from '@/hooks/useAppNavigation';
import ToastSurface from '../ToastSurface';
import MobileLayout from '../MobileLayout';
import sendAiGenScreenActionAnalytics from '@/utils/analytics/sendAiGenScreenActionAnalytics';
import getScreenType from '@/utils/analytics/getScreenType';
import { AiGenScreenAction } from '@/types/analytics';
import useStyles from './styles';

const DesktopLayout = lazy(() => import('../DesktopLayout'));

const PortfolioShell = lazy(() => import('@/components/PortfolioShell'));

const selector = (store: IAppStore) => ({
  screen: store.screen,
  isProfileOpen: store.isProfileOpen,
  isSettingsOpen: store.isSettingsOpen,
  isCreditsOpen: store.isCreditsOpen,
});

const AppShell = () => {
  const classes = useStyles();
  const { isMobile } = useDeviceType();
  const isPortfolioApp = checkIsPortfolioApp();
  const { isAuthorized } = useAuth();
  const { screen, isProfileOpen, isSettingsOpen, isCreditsOpen } =
    useAppStore(selector);

  const showPortfolio = isPortfolioApp && isMobile;

  const portfolioActive =
    showPortfolio &&
    (isSettingsOpen ||
      isCreditsOpen ||
      (isProfileOpen && screen === Screen.Library));

  const onBackFromSettings = useCallback(
    () => setAppStore({ isSettingsOpen: false }),
    []
  );
  const onBackFromProfile = useCallback(
    () => setAppStore({ isProfileOpen: false }),
    []
  );
  const onBackFromCredits = useCallback(
    () => setAppStore({ isCreditsOpen: false }),
    []
  );

  useAppNavigation({
    onBackFromSettings,
    onBackFromProfile,
    onBackFromCredits,
  });

  const handleOpenSettings = useCallback(() => {
    setAppStore({ isSettingsOpen: true });
    pushBrowserNav('settings');
  }, []);

  const handleOpenProfile = useCallback(() => {
    if (!isAuthorized) {
      openRegistrationModal();
      return;
    }
    setAppStore({ isProfileOpen: true });
    pushBrowserNav('profile');
    sendAiGenScreenActionAnalytics({
      action: AiGenScreenAction.PROFILE_OPEN,
      screen_type: getScreenType(),
    });
  }, [isAuthorized]);

  return (
    <div className={classes.root}>
      <AppLayout
        header={
          portfolioActive ? undefined : (
            <Header
              onSettingsClick={handleOpenSettings}
              onAvatarClick={handleOpenProfile}
            />
          )
        }
        isMobile={isMobile}
      >
        {portfolioActive ? (
          <Suspense fallback={null}>
            <PortfolioShell />
          </Suspense>
        ) : isMobile ? (
          <MobileLayout />
        ) : (
          <Suspense fallback={null}>
            <DesktopLayout />
          </Suspense>
        )}
      </AppLayout>
      <ResultOverlay />
      <ToastSurface />
    </div>
  );
};

export default AppShell;
